import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle2, AlertCircle } from "lucide-react";
import Logo from "../components/Logo";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import { apiClient, formatApiError, useAuth } from "../lib/auth";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const { refresh } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-5" data-testid="reset-page">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 text-center shadow-[0_20px_50px_-15px_rgba(15,23,42,0.12)]">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-red-50 border border-red-200 flex items-center justify-center mb-5">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Invalid reset link</h1>
          <p className="text-sm text-slate-500 mb-5">This link doesn't include a reset token. Request a new one from the login page.</p>
          <button onClick={() => navigate("/forgot-password")} className="bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-5 py-2.5">
            Request a new link
          </button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e?.preventDefault();
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiClient.post("/auth/password-reset/confirm", { token, new_password: password });
      await refresh();
      setDone(true);
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (err) {
      setError(formatApiError(err, "Could not reset password."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 relative overflow-hidden" data-testid="reset-page">
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-[#1D6AFF]/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-md mx-auto px-5 pt-10 pb-16">
        <button
          data-testid="reset-back"
          onClick={() => navigate("/login")}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>

        <div className="flex justify-center mb-7">
          <Logo size="lg" />
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl p-7 sm:p-8 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.12)]">
          {!done ? (
            <>
              <h1 className="font-display text-3xl font-bold mb-2 text-slate-900">Set a new password</h1>
              <p className="text-sm text-slate-500 mb-7">Pick something at least 6 characters long.</p>

              <form onSubmit={submit} className="space-y-4">
                <PwField
                  testId="reset-password"
                  label="New password"
                  value={password}
                  onChange={setPassword}
                  showPw={showPw}
                  onToggle={() => setShowPw((v) => !v)}
                />
                <PasswordStrengthMeter password={password} />
                <PwField
                  testId="reset-confirm"
                  label="Confirm new password"
                  value={confirm}
                  onChange={setConfirm}
                  showPw={showPw}
                />

                {error && <div data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</div>}

                <button
                  type="submit"
                  data-testid="reset-submit"
                  disabled={busy}
                  className="w-full bg-[#1D6AFF] hover:bg-[#1552C6] disabled:opacity-70 text-white font-semibold rounded-xl py-3.5 transition-all shadow-[0_14px_30px_-8px_rgba(29,106,255,0.4)]"
                >
                  {busy ? "Resetting…" : "Reset password & sign in"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="reset-success" className="text-center py-2">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 border border-emerald-300 flex items-center justify-center mb-5">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Password reset</h1>
              <p className="text-sm text-slate-500">Signing you in and taking you to the dashboard…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PwField = ({ testId, label, value, onChange, showPw, onToggle }) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">{label}</label>
    <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-3 focus-within:border-[#1D6AFF] focus-within:bg-white transition-all">
      <Lock className="w-4 h-4 text-slate-400 mr-2.5 shrink-0" />
      <input
        data-testid={testId}
        type={showPw ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="••••••••"
        className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 text-sm"
      />
      {onToggle && (
        <button type="button" onClick={onToggle} className="text-slate-400 hover:text-slate-700">
          {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      )}
    </div>
  </div>
);
